import type JSZip from 'jszip';
import type { FullStlViewerFile } from '@odo/shared-ui';
import { UserFacingError } from '../../../../../utils/userFacingError';
import {
    baseName,
    bestArchMeshes,
    hasPlyMesh,
    imageEntryPaths,
    imageMimeType,
    stlMeshPaths,
    type ArchMeshPaths
} from './scanEntries';

export interface ScanImage {
    name: string;
    blob: Blob;
}

const SINGLE_IMAGE_NAME = 'image';

async function openZip(blob: Blob, unreadableText: string): Promise<JSZip> {
    const { default: JSZipImpl } = await import('jszip');
    try {
        return await JSZipImpl.loadAsync(await blob.arrayBuffer());
    } catch {
        throw new UserFacingError(unreadableText);
    }
}

function filePaths(zip: JSZip): string[] {
    return Object.values(zip.files)
        .filter((entry) => !entry.dir)
        .map((entry) => entry.name);
}

function isImageContentType(contentType: string | null): boolean {
    const mime = contentType?.split(';')[0].trim().toLowerCase() ?? '';
    return mime === 'image/jpeg' || mime === 'image/png';
}

// Reading the central directory is cheap; only the entries we keep are inflated.
export async function extractImages(blob: Blob, contentType: string | null): Promise<ScanImage[]> {
    if (isImageContentType(contentType)) return [{ name: SINGLE_IMAGE_NAME, blob }];
    const zip = await openZip(blob, "The image archive couldn't be opened.");
    const images = await Promise.all(
        imageEntryPaths(filePaths(zip)).map(async (path) => {
            const entry = zip.file(path);
            if (!entry) return null;
            const bytes = await entry.async('arraybuffer');
            return { name: path, blob: new Blob([bytes], { type: imageMimeType(path) ?? '' }) };
        })
    );
    return images.filter((image): image is ScanImage => image !== null);
}

export interface ScanMeshSet {
    files: FullStlViewerFile[];
    preselect: ArchMeshPaths;
    hasPly: boolean;
    loadMesh: (id: string) => Promise<Blob>;
}

const STL_MIME = 'model/stl';

// Entries are inflated on first view: an iDE carries a dozen or more STL variants of tens of MB each.
export async function openScanMeshes(blob: Blob): Promise<ScanMeshSet> {
    const zip = await openZip(blob, "The 3D files couldn't be opened.");
    const paths = filePaths(zip);
    const stlPaths = stlMeshPaths(paths);
    return {
        files: stlPaths.map((path) => ({
            id: path,
            name: baseName(path),
            createdAt: zip.file(path)?.date.toISOString()
        })),
        preselect: bestArchMeshes(stlPaths),
        hasPly: hasPlyMesh(paths),
        loadMesh: async (id) => {
            const entry = stlPaths.includes(id) ? zip.file(id) : null;
            if (!entry) throw new UserFacingError("This 3D file isn't in the export.");
            return new Blob([await entry.async('blob')], { type: STL_MIME });
        }
    };
}
